import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Stack,
  TextField
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ko } from 'date-fns/locale'
import { format, startOfDay, endOfDay, differenceInDays, isSameDay } from 'date-fns'
import { easyFarmAPI, SensorData } from '../lib/api'

interface SensorChartProps {
  deviceId?: string
}

type TimeCandle = '10s' | '1m' | '5m' | '1h' | '4h' | '1d'

const SensorChart: React.FC<SensorChartProps> = ({ deviceId = 'farmlink-001' }) => {
  const [rawData, setRawData] = useState<any[]>([])
  const today = new Date()
  const [startDate, setStartDate] = useState<Date | null>(today)
  const [endDate, setEndDate] = useState<Date | null>(today)
  const [timeCandle, setTimeCandle] = useState<TimeCandle>('10s')
  const [loading, setLoading] = useState(true)
  const lastDataIdRef = useRef<number | null>(null)
  const isInitialLoadRef = useRef(true)
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // 단일 날짜 선택 여부 확인
  const isSingleDate = useMemo(() => {
    if (!startDate || !endDate) return false
    return isSameDay(startDate, endDate)
  }, [startDate, endDate])

  // 시간봉 집계 함수
  const aggregateData = (data: any[], candle: TimeCandle): any[] => {
    if (candle === '10s' || data.length === 0) {
      return data
    }

    const candleMs: Record<TimeCandle, number> = {
      '10s': 10000,
      '1m': 60 * 1000,
      '5m': 5 * 60 * 1000,
      '1h': 60 * 60 * 1000,
      '4h': 4 * 60 * 60 * 1000,
      '1d': 24 * 60 * 60 * 1000,
    }

    const interval = candleMs[candle]
    const grouped = new Map<number, any[]>()

    // 데이터를 시간봉 단위로 그룹화
    data.forEach((item) => {
      const candleTime = Math.floor(item.timestamp / interval) * interval
      if (!grouped.has(candleTime)) {
        grouped.set(candleTime, [])
      }
      grouped.get(candleTime)!.push(item)
    })

    // 각 그룹의 평균 계산
    const aggregated: any[] = []
    grouped.forEach((items, candleTime) => {
      const count = items.length
      const avg = {
        timestamp: candleTime,
        time: new Date(candleTime).toLocaleTimeString('ko-KR', {
          hour: '2-digit',
          minute: '2-digit',
        }),
        fullTime: new Date(candleTime).toLocaleString('ko-KR'),
        created_at: new Date(candleTime).toISOString(),
        수분: items.reduce((sum, item) => sum + (item.수분 || 0), 0) / count,
        온도: items.reduce((sum, item) => sum + (item.온도 || 0), 0) / count,
        습도: items.reduce((sum, item) => sum + (item.습도 || 0), 0) / count,
        조도: items.reduce((sum, item) => sum + (item.조도 || 0), 0) / count,
        id: items[items.length - 1].id, // 가장 최신 데이터의 ID
      }
      aggregated.push(avg)
    })

    return aggregated.sort((a, b) => a.timestamp - b.timestamp)
  }

  // 집계된 데이터
  const data = useMemo(() => {
    return aggregateData(rawData, timeCandle)
  }, [rawData, timeCandle])

  const fetchSensorData = useCallback(async (isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setLoading(true)
        isInitialLoadRef.current = true
      }

      if (!startDate || !endDate) {
        setLoading(false)
        return
      }

      // 날짜 범위 설정 (하루의 시작과 끝)
      const start = startOfDay(startDate)
      const end = endOfDay(endDate)
      const daysDiff = differenceInDays(end, start)
      
      // 최대 1년까지만 허용
      if (daysDiff > 365) {
        setLoading(false)
        setRawData([])
        return
      }

      // 날짜 범위에 따라 limit 설정
      // 단일 날짜: 하루에 10초마다 데이터가 들어온다면 최대 8640개 필요
      // 여러 날짜: 날짜 수에 비례하여 증가
      let limit: number
      if (daysDiff === 0) {
        // 단일 날짜: 하루 전체 데이터를 가져오기 위해 충분히 큰 값
        limit = 10000
      } else if (daysDiff <= 1) {
        limit = 20000
      } else if (daysDiff <= 7) {
        limit = 50000
      } else {
        limit = 100000
      }

      const startDateISO = start.toISOString()
      const endDateISO = end.toISOString()
      
      const response = await easyFarmAPI.getSensorData({
        limit,
        device_id: deviceId,
        start_date: startDateISO,
        end_date: endDateISO
      })

      if (!response.success || !response.data) {
        console.error('Sensor data fetch error:', response.error)
        if (isInitial) {
          setRawData([])
        }
        return
      }

      const startTime = start.getTime()
      const endTime = end.getTime()

      if (isInitial || isInitialLoadRef.current) {
        // 초기 로드: 전체 데이터 포맷팅
        // 서버에서 timestamp 또는 created_at 필드를 사용할 수 있으므로 둘 다 확인
        const formattedData = response.data
          .map((item: SensorData) => {
            // timestamp 필드가 있으면 사용, 없으면 created_at 사용
            const dateString = (item as any).timestamp || item.created_at
            const itemDate = new Date(dateString)
            return {
              time: itemDate.toLocaleTimeString('ko-KR', {
                hour: '2-digit',
                minute: '2-digit',
              }),
              fullTime: itemDate.toLocaleString('ko-KR'),
              timestamp: itemDate.getTime(),
              created_at: item.created_at,
              수분: item.soil_moisture,
              온도: item.temperature,
              습도: item.humidity,
              조도: item.light_intensity,
              id: item.id,
            }
          })
          .filter((item) => item.timestamp >= startTime && item.timestamp <= endTime)
          .sort((a, b) => a.timestamp - b.timestamp)
          // 중복된 타임스탬프 제거
          .filter((item, index, arr) => {
            if (index === 0) return true
            return item.timestamp !== arr[index - 1].timestamp
          })
        
        setRawData(formattedData)
        if (formattedData.length > 0) {
          lastDataIdRef.current = formattedData[formattedData.length - 1].id
        }
        isInitialLoadRef.current = false
      } else {
        // 실시간 업데이트: 새로운 데이터만 추가 (10초봉일 때만)
        if (!response.data || response.data.length === 0) {
          return
        }
        
        setRawData((prevData) => {
          const newData = response.data!
            .filter((item: SensorData) => {
              // timestamp 필드가 있으면 사용, 없으면 created_at 사용
              const dateString = (item as any).timestamp || item.created_at
              const itemTime = new Date(dateString).getTime()
              return (
                (lastDataIdRef.current === null || item.id > lastDataIdRef.current) &&
                itemTime >= startTime &&
                itemTime <= endTime
              )
            })
            .map((item: SensorData) => {
              // timestamp 필드가 있으면 사용, 없으면 created_at 사용
              const dateString = (item as any).timestamp || item.created_at
              const itemDate = new Date(dateString)
              return {
                time: itemDate.toLocaleTimeString('ko-KR', {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
                fullTime: itemDate.toLocaleString('ko-KR'),
                timestamp: itemDate.getTime(),
                created_at: item.created_at,
                수분: item.soil_moisture,
                온도: item.temperature,
                습도: item.humidity,
                조도: item.light_intensity,
                id: item.id,
              }
            })
          
          if (newData.length > 0) {
            const combinedData = [...prevData, ...newData]
            const filteredData = combinedData.filter((item) => {
              return item.timestamp >= startTime && item.timestamp <= endTime
            })
            const sortedData = filteredData.sort((a, b) => a.timestamp - b.timestamp)
              .filter((item, index, arr) => {
                if (index === 0) return true
                return item.timestamp !== arr[index - 1].timestamp
              })
            
            if (sortedData.length > 0) {
              lastDataIdRef.current = sortedData[sortedData.length - 1].id
            }
            
            return sortedData
          }
          
          return prevData.filter((item) => {
            return item.timestamp >= startTime && item.timestamp <= endTime
          })
        })
      }
    } catch (error) {
      console.error('Chart data fetch error:', error)
    } finally {
      if (isInitial) {
        setLoading(false)
      }
    }
  }, [startDate, endDate, deviceId])

  useEffect(() => {
    // 날짜 범위나 디바이스가 변경되면 초기 로드로 리셋
    isInitialLoadRef.current = true
    lastDataIdRef.current = null
    
    // 기존 인터벌 정리
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
    }
    
    // 초기 데이터 로드
    fetchSensorData(true)
    
    // 10초봉일 때만 실시간 업데이트
    if (timeCandle === '10s' && isSingleDate) {
      refreshIntervalRef.current = setInterval(() => {
        fetchSensorData(false)
      }, 10000)
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [startDate, endDate, deviceId, timeCandle, isSingleDate, fetchSensorData])

  if (loading) {
    return (
      <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>차트 데이터를 불러오는 중...</Typography>
      </Box>
    )
  }

  if (data.length === 0) {
    return (
      <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          표시할 센서 데이터가 없습니다.
        </Typography>
      </Box>
    )
  }

  // 최대 1년 전까지 선택 가능
  const minDate = new Date()
  minDate.setFullYear(minDate.getFullYear() - 1)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
      <Box>
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <DatePicker
            label="시작 날짜"
            value={startDate}
            onChange={(newValue) => {
              setStartDate(newValue)
              // 시작 날짜가 종료 날짜보다 늦으면 종료 날짜도 조정
              if (newValue && endDate && newValue > endDate) {
                setEndDate(newValue)
              }
            }}
            minDate={minDate}
            maxDate={new Date()}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="종료 날짜"
            value={endDate}
            onChange={(newValue) => {
              setEndDate(newValue)
              // 종료 날짜가 시작 날짜보다 이전이면 시작 날짜도 조정
              if (newValue && startDate && newValue < startDate) {
                setStartDate(newValue)
              }
            }}
            minDate={startDate || minDate}
            maxDate={new Date()}
            slotProps={{ textField: { size: 'small' } }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>시간봉</InputLabel>
            <Select
              value={timeCandle}
              label="시간 선택"
              onChange={(e) => setTimeCandle(e.target.value as TimeCandle)}
            >
              <MenuItem value="10s" disabled={!isSingleDate}>10s</MenuItem>
              <MenuItem value="1m" disabled={!isSingleDate}>1분</MenuItem>
              <MenuItem value="5m" disabled={!isSingleDate}>5분</MenuItem>
              <MenuItem value="1h">1시간</MenuItem>
              <MenuItem value="4h">4시간</MenuItem>
              <MenuItem value="1d">일</MenuItem>
            </Select>
          </FormControl>
        </Stack>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="timestamp"
            type="number"
            domain={['dataMin', 'dataMax']}
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => {
              const date = new Date(value)
              // 시간봉에 따라 다른 형식으로 표시
              if (timeCandle === '1d') {
                return format(date, 'MM/dd')
              } else if (timeCandle === '4h' || timeCandle === '1h') {
                return format(date, 'MM/dd HH:mm')
              } else {
                return format(date, 'HH:mm')
              }
            }}
            interval="preserveStartEnd"
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            labelFormatter={(value) => {
              const date = new Date(value)
              return date.toLocaleString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
            }}
            formatter={(value, name) => [
              typeof value === 'number' ? value.toFixed(1) : value,
              name
            ]}
          />
          <Legend />
          <Line
            type="linear"
            dataKey="수분"
            stroke="#4CAF50"
            strokeWidth={2}
            dot={timeCandle === '10s' ? false : { fill: '#4CAF50', r: 3 }}
            isAnimationActive={false}
            name="토양 수분 (%)"
            connectNulls={false}
          />
          <Line
            type="linear"
            dataKey="온도"
            stroke="#FF9800"
            strokeWidth={2}
            dot={timeCandle === '10s' ? false : { fill: '#FF9800', r: 3 }}
            isAnimationActive={false}
            name="온도 (°C)"
            connectNulls={false}
          />
          <Line
            type="linear"
            dataKey="습도"
            stroke="#2196F3"
            strokeWidth={2}
            dot={timeCandle === '10s' ? false : { fill: '#2196F3', r: 3 }}
            isAnimationActive={false}
            name="습도 (%)"
            connectNulls={false}
          />
          <Line
            type="linear"
            dataKey="조도"
            stroke="#9C27B0"
            strokeWidth={2}
            dot={timeCandle === '10s' ? false : { fill: '#9C27B0', r: 3 }}
            isAnimationActive={false}
            name="조도 (ph)"
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
      </Box>
    </LocalizationProvider>
  )
}

export default SensorChart
